import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getArticle } from "../lib/api";
import { absoluteUrl, canonicalUrl, ROUTE_SEO, SITE_NAME, SITE_URL, SOCIAL_IMAGE } from "../seo/config";
import { removeCanonical, removeStructuredData, setCanonical, setMeta, setSocialMetadata, setStructuredData } from "../seo/head";
import { useLocale } from "./LocaleProvider";

const robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const noIndex = "noindex, nofollow, noarchive";

function pageSchema(name, description, canonical, label, locale) {
  const language = locale === "es" ? "es" : "pt-BR";
  const graph = [{
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name,
    description,
    inLanguage: language,
    isPartOf: { "@id": `${SITE_URL}/#website` },
  }];
  if (canonical !== SITE_URL) graph.push({
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: locale === "es" ? "Inicio" : "Início", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: label, item: canonical },
    ],
  });
  return { "@context": "https://schema.org", "@graph": graph };
}

function articleSchema(article, canonical) {
  const image = absoluteUrl(article.cover_image);
  return { "@context": "https://schema.org", "@graph": [{
    "@type": "Article",
    "@id": `${canonical}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: article.title,
    description: article.excerpt,
    image: [image],
    datePublished: article.created_at,
    dateModified: article.updated_at || article.created_at,
    articleSection: article.category || "Artigos técnicos",
    inLanguage: "pt-BR",
    author: { "@type": "Organization", name: article.author || SITE_NAME, url: SITE_URL },
    publisher: { "@id": `${SITE_URL}/#organization` },
    isAccessibleForFree: true,
  }, {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Início", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Artigos", item: `${SITE_URL}/artigos` },
      { "@type": "ListItem", position: 3, name: article.title, item: canonical },
    ],
  }] };
}

export const LocaleMetadata = () => {
  const { pathname } = useLocation();
  const { locale } = useLocale();
  const [article, setArticle] = useState(null);

  useEffect(() => {
    const slug = pathname.startsWith("/artigos/") ? pathname.slice("/artigos/".length) : "";
    setArticle(null);
    if (!slug) return undefined;
    let active = true;
    getArticle(slug).then((data) => active && setArticle(data)).catch(() => {});
    return () => { active = false; };
  }, [pathname]);

  useEffect(() => {
    const isAdmin = pathname.startsWith("/admin");
    const isArticle = pathname.startsWith("/artigos/");
    const config = ROUTE_SEO[pathname] || (isArticle ? ROUTE_SEO["/artigos"] : null);
    if (isAdmin || !config) {
      document.title = isAdmin ? "Administração | Gi Inovações" : "Página não encontrada | Gi Inovações";
      setMeta("name", "robots", noIndex);
      setMeta("name", "googlebot", noIndex);
      removeCanonical();
      removeStructuredData();
      return;
    }

    const index = locale === "es" ? 1 : 0;
    const canonical = canonicalUrl(pathname);
    const title = article?.title ? `${article.title} | ${SITE_NAME}` : config.title[index];
    const description = article?.excerpt || config.description[index];
    const image = article?.cover_image ? absoluteUrl(article.cover_image) : SOCIAL_IMAGE;
    const imageAlt = article?.cover_alt || article?.title || SITE_NAME;

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", article?.published === false ? noIndex : robots);
    setMeta("name", "googlebot", article?.published === false ? noIndex : robots);
    setCanonical(canonical);
    setSocialMetadata({ title, description, canonical, image, imageAlt, type: article ? "article" : "website", locale });
    setStructuredData(article
      ? articleSchema(article, canonical)
      : pageSchema(title, description, canonical, config.label[index], locale));
  }, [article, locale, pathname]);

  return null;
};